import { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionPanel,
  Box,
  Button,
  Collapsible,
  Heading,
  Page,
  PageContent,
  Paragraph,
  Text,
} from 'grommet';

import LeagueCard from '../LeagueCard';
import ConnectLeagueForm from '../ConnectLeagueForm';
import { CURRENT_SEASON } from '../config';
import { listConnections, removeConnection } from '../connections';

// The pals' connected teams from other leagues — previously the bulk of the
// homepage, now its own page while the elimination league takes the spotlight.
export default function Pals() {
  const [pals, setPals] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [showAddForm, setShowAddForm] = useState(false);

  const reload = useCallback(async () => {
    setStatus('loading');
    try {
      const data = await listConnections(CURRENT_SEASON);
      setPals(data);
      setStatus('ready');
    } catch (e) {
      setStatus('error');
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleRemove = async (id) => {
    try {
      await removeConnection(id);
      setPals(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      // Leave the list unchanged if the delete fails.
    }
  };

  return (
    <Page kind="narrow" pad="medium">
      <PageContent>
        <Heading level={1} margin={{ bottom: 'none' }}>Pals' leagues</Heading>
        <Paragraph fill>
          Follow how your pals are doing in their other ESPN leagues this season.
        </Paragraph>

        <Box margin={{ vertical: 'medium' }} gap="small" flex={false}>
          <Button
            size="small"
            alignSelf="start"
            label={showAddForm ? 'Cancel' : '+ Add your team'}
            onClick={() => setShowAddForm(prev => !prev)}
          />
          <Collapsible open={showAddForm}>
            <ConnectLeagueForm
              season={CURRENT_SEASON}
              onAdded={() => { setShowAddForm(false); reload(); }}
            />
          </Collapsible>
        </Box>

        {status === 'loading' && <Text>Loading pals…</Text>}
        {status === 'error' && (
          <Text color="status-critical">
            Couldn't load connected teams. Check the Supabase configuration.
          </Text>
        )}
        {status === 'ready' && pals.length === 0 && (
          <Text>No teams connected yet. Add yours above!</Text>
        )}
        {status === 'ready' && pals.length > 0 && (
          <Accordion multiple={true} pad="medium" flex={false}>
            {pals.map(pal => (
              <AccordionPanel key={pal.id} label={pal.pal_name}>
                <Box pad="medium" margin="small" gap="small">
                  <LeagueCard ownerName={pal.pal_name} leagueID={pal.league_id} teamID={pal.team_id} />
                  <Button
                    size="small"
                    label="Remove"
                    onClick={() => handleRemove(pal.id)}
                    alignSelf="start"
                  />
                </Box>
              </AccordionPanel>
            ))}
          </Accordion>
        )}
      </PageContent>
    </Page>
  );
}
